import { useDebouncedValue } from '@mantine/hooks';
import { VMultiSelect } from '@vision/ui/components';
import { OptionsGroup, SimpleTagGroupItem } from '@vision/ui/interfaces';
import { useLazyGetSchemaTagValuesQuery } from '@vision/ui/services';
import qs from 'qs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SCHEMA_TAG_VALUES_PER_PAGE = 20;

function mergeSchemaTagItems(
  currentItems: SimpleTagGroupItem[],
  nextItems: SimpleTagGroupItem[],
): SimpleTagGroupItem[] {
  return Array.from(new Map(currentItems.concat(nextItems).map((item) => [item.id, item])).values());
}

function mapSchemaTagValuesToGroupedSelectItems(items: SimpleTagGroupItem[]): OptionsGroup[] {
  const groupMap = items.reduce<Record<string, OptionsGroup>>((acc, item) => {
    const group = item.tag_group_name || item.name;

    if (!acc[group]) {
      acc[group] = {
        group,
        items: [],
      };
    }

    acc[group].items.push({
      value: item.id,
      label: item.name,
    });

    return acc;
  }, {});

  return Object.values(groupMap);
}

interface ReportsDetailGraphicCompareSchemaValuesProps {
  accountId: string;
  error: React.ReactNode;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
  onChange: (value: string[]) => void;
  schemaFieldIds: string[];
  value: string[];
}

export function ReportsDetailGraphicCompareSchemaValues({
  accountId,
  error,
  onBlur,
  onChange,
  schemaFieldIds,
  value,
}: ReportsDetailGraphicCompareSchemaValuesProps) {
  const { t } = useTranslation();
  const schemaFieldIdsKey = schemaFieldIds.join(',');
  const [schemaTagItems, setSchemaTagItems] = useState<SimpleTagGroupItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [getSchemaTagValues, { data: schemaTagValuesResponse, isFetching }] = useLazyGetSchemaTagValuesQuery();
  const [
    getSelectedSchemaTagValues,
    { data: selectedSchemaTagValuesResponse, isFetching: isSelectedSchemaTagValuesFetching },
  ] = useLazyGetSchemaTagValuesQuery();

  const handleGetSchemaTagValues = useCallback(
    (page: number) => {
      if (!accountId || schemaFieldIds.length === 0) {
        return;
      }

      getSchemaTagValues({
        accountId,
        schemaFieldIds,
        query: qs.stringify({
          page,
          per_page: SCHEMA_TAG_VALUES_PER_PAGE,
          ...(debouncedSearchQuery ? { search_string: debouncedSearchQuery } : {}),
        }),
      });
    },
    [accountId, debouncedSearchQuery, getSchemaTagValues, schemaFieldIdsKey],
  );

  const handleLoadMore = () => {
    const pagination = schemaTagValuesResponse?.meta?.pagination;

    if (!pagination?.next) {
      return;
    }

    handleGetSchemaTagValues(pagination.current + 1);
  };

  useEffect(() => {
    setSchemaTagItems([]);
    handleGetSchemaTagValues(1);
  }, [handleGetSchemaTagValues]);

  useEffect(() => {
    if (!schemaTagValuesResponse?.data) {
      return;
    }

    setSchemaTagItems((currentItems) => mergeSchemaTagItems(currentItems, schemaTagValuesResponse.data));
  }, [schemaTagValuesResponse]);

  useEffect(() => {
    if (!accountId || schemaFieldIds.length === 0 || value.length === 0) {
      return;
    }

    getSelectedSchemaTagValues({
      accountId,
      schemaFieldIds,
      query: qs.stringify({
        tag_ids: value.join(','),
        per_page: 50,
      }),
    });
  }, [accountId, getSelectedSchemaTagValues, schemaFieldIdsKey, value]);

  useEffect(() => {
    if (!selectedSchemaTagValuesResponse?.data) {
      return;
    }

    setSchemaTagItems((currentItems) => mergeSchemaTagItems(currentItems, selectedSchemaTagValuesResponse.data));
  }, [selectedSchemaTagValuesResponse]);

  const schemaTagSelectItems = useMemo(() => mapSchemaTagValuesToGroupedSelectItems(schemaTagItems), [schemaTagItems]);

  return (
    <VMultiSelect
      id="report-compare-schema-values"
      data-testid="input-report-compare-schema-values"
      label={t('values')}
      data={schemaTagSelectItems}
      loading={isFetching}
      isValuesPillLoading={isSelectedSchemaTagValuesFetching}
      searchable={true}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onScrollToBottom={handleLoadMore}
      onSearchChange={setSearchQuery}
      error={error}
      withSelectAll={false}
      showAllSelectedPill={false}
    />
  );
}
